import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Minus, Plus } from "lucide-react";
import { money, FLAVOR_OPTIONS, SODA_OPTIONS, DIP_OPTIONS, COMBO_PRICE } from "../../lib/menu";
import { useCart } from "../../context/CartContext";

const PickGroup = ({ label, count, options, values, onChange, testid }) => (
  <div className="space-y-2">
    <Label className="font-heading text-lg text-neon-green uppercase tracking-wide">{label}</Label>
    <div className="grid gap-2">
      {[...Array(count)].map((_, i) => (
        <Select key={i} value={values[i] || ""} onValueChange={(v) => onChange(i, v)}>
          <SelectTrigger data-testid={`${testid}-${i}`} className="bg-black border-neon-green/30 text-white">
            <SelectValue placeholder={`Choose ${label.toLowerCase()} ${count > 1 ? `#${i + 1}` : ""}`} />
          </SelectTrigger>
          <SelectContent className="bg-elevated border-neon-green/30 text-white">
            {options.map((o) => (
              <SelectItem key={o} value={o} className="focus:bg-neon-green/20 focus:text-white">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}
    </div>
  </div>
);

export const ItemDialog = ({ item, open, onClose, flavors: flavorOptions, sodas: sodaOptions, dips: dipOptions, comboPrice }) => {
  const flavorList = flavorOptions && flavorOptions.length ? flavorOptions : FLAVOR_OPTIONS;
  const sodaList = sodaOptions && sodaOptions.length ? sodaOptions : SODA_OPTIONS;
  const sauceList = dipOptions && dipOptions.length ? dipOptions : DIP_OPTIONS;
  const comboCents = Number.isFinite(comboPrice) ? comboPrice : COMBO_PRICE;
  const { addItem } = useCart();
  const [variantIdx, setVariantIdx] = useState(0);
  const [flavors, setFlavors] = useState([]);
  const [sodas, setSodas] = useState([]);
  const [sauces, setSauces] = useState([]);
  const [combo, setCombo] = useState(false);
  const [sauceToss, setSauceToss] = useState(false);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setVariantIdx(0); setFlavors([]); setSodas([]); setSauces([]);
      setCombo(false); setSauceToss(false); setQty(1); setNotes("");
    }
  }, [open, item]);

  // Reset flavor selections when the size (variant) changes — flavor count varies by size
  useEffect(() => { setFlavors([]); }, [variantIdx]);

  const variant = item?.variants?.[variantIdx];
  const flavorPick = (variant && variant.flavors) || item?.flavorPick || (sauceToss ? 1 : 0);
  const comboSodas = combo || variant?.withSoda ? 1 : 0;
  const sodaPick = item?.sodaPick || comboSodas;
  const saucePick = item?.saucePick || 0;

  const unitPrice = useMemo(() => {
    if (!item) return 0;
    let base = variant ? variant.price : item.price;
    if (combo) base += comboCents;
    if (sauceToss) base += 100;
    return base;
  }, [item, variant, combo, sauceToss, comboCents]);

  if (!item) return null;

  const setAt = (setter) => (i, v) => setter((arr) => { const c = [...arr]; c[i] = v; return c; });

  const complete =
    (flavorPick === 0 || flavors.filter(Boolean).length >= flavorPick) &&
    (sodaPick === 0 || sodas.filter(Boolean).length >= sodaPick) &&
    (saucePick === 0 || sauces.filter(Boolean).length >= saucePick);

  const handleAdd = () => {
    const options = [];
    if (variant) options.push(`Size: ${variant.label}`);
    if (item.fries) options.push(`Includes: ${item.fries}`);
    if (combo) options.push("Combo (+$4): Fries + Soda");
    if (sauceToss) options.push("Sauce toss (+$1)");
    flavors.filter(Boolean).slice(0, flavorPick).forEach((f) => options.push(`Flavor: ${f}`));
    sodas.filter(Boolean).slice(0, sodaPick).forEach((s) => options.push(`Soda: ${s}`));
    sauces.filter(Boolean).slice(0, saucePick).forEach((s) => options.push(`Sauce: ${s}`));
    if (notes.trim()) options.push(`Note: ${notes.trim()}`);

    const label = variant ? `${item.name} — ${variant.label}` : item.name;
    addItem({ name: label, quantity: qty, unit_price_cents: unitPrice, options });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-elevated border-neon-green/30 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-4xl text-neon-green uppercase tracking-wide neon-glow-green">{item.name}</DialogTitle>
          <DialogDescription className="font-body text-sm text-neutral-400">{item.desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {item.variants && (
            <div className="space-y-2">
              <Label className="font-heading text-lg text-neon-green uppercase tracking-wide">Size</Label>
              <RadioGroup value={String(variantIdx)} onValueChange={(v) => setVariantIdx(Number(v))} className="grid gap-2">
                {item.variants.map((v, i) => (
                  <label key={v.label} htmlFor={`var-${i}`} className="flex items-center justify-between border border-neon-green/20 px-4 py-3 cursor-pointer hover:border-neon-green transition-colors">
                    <span className="flex items-center gap-3">
                      <RadioGroupItem value={String(i)} id={`var-${i}`} data-testid={`variant-${i}`} className="border-neon-green text-neon-green" />
                      <span className="font-body">{v.label}</span>
                    </span>
                    <span className="font-heading text-xl text-neon-yellow">{money(v.price)}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}

          {item.comboAvailable && (
            <label className="flex items-center gap-3 border border-neon-yellow/30 px-4 py-3 cursor-pointer">
              <Checkbox checked={combo} onCheckedChange={(c) => setCombo(!!c)} data-testid="combo-checkbox" className="border-neon-yellow data-[state=checked]:bg-neon-yellow data-[state=checked]:text-black" />
              <span className="font-body text-sm">Make it a combo <span className="text-neon-yellow font-heading text-lg">+{money(comboCents)}</span> — includes fries & can soda</span>
            </label>
          )}

          {item.sauceToss && (
            <label className="flex items-center gap-3 border border-neon-yellow/30 px-4 py-3 cursor-pointer">
              <Checkbox checked={sauceToss} onCheckedChange={(c) => setSauceToss(!!c)} data-testid="sauce-toss-checkbox" className="border-neon-yellow data-[state=checked]:bg-neon-yellow data-[state=checked]:text-black" />
              <span className="font-body text-sm">Toss it in any sauce <span className="text-neon-yellow font-heading text-lg">+$1</span></span>
            </label>
          )}

          {flavorPick > 0 && (
            <PickGroup label="Flavor" count={flavorPick} options={flavorList} values={flavors} onChange={setAt(setFlavors)} testid="flavor-select" />
          )}
          {sodaPick > 0 && (
            <PickGroup label="Soda" count={sodaPick} options={sodaList} values={sodas} onChange={setAt(setSodas)} testid="soda-select" />
          )}
          {saucePick > 0 && (
            <PickGroup label="Sauce" count={saucePick} options={sauceList} values={sauces} onChange={setAt(setSauces)} testid="sauce-select" />
          )}

          <div className="space-y-2">
            <Label className="font-body text-sm text-neutral-400">Special instructions (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="item-notes" placeholder="Extra crispy, no celery, etc." className="bg-black border-neon-green/30 text-white resize-none" />
          </div>

          <div className="flex items-center gap-4">
            <span className="font-heading text-lg text-neon-green uppercase">Qty</span>
            <div className="flex items-center gap-3 border border-neon-green/30">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" className="p-2 text-neon-green hover:bg-neon-green/10"><Minus size={16} /></button>
              <span className="font-heading text-xl w-8 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-plus" className="p-2 text-neon-green hover:bg-neon-green/10"><Plus size={16} /></button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleAdd}
            disabled={!complete}
            data-testid="add-to-cart-btn"
            className="w-full font-heading text-xl tracking-wide bg-neon-green text-black hover:bg-neon-yellow disabled:opacity-40 disabled:cursor-not-allowed py-6"
          >
            {complete ? `Add to Cart · ${money(unitPrice * qty)}` : "Complete your choices"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ItemDialog;
